/**
 * @file This file contain the code necessary to create the structure of the
 * exported plagiarism report.
 */
import * as moment from 'moment';
import { Maybe } from 'purify-ts';
import * as api from '@codegrade/api-models';
import { array, assert, datetime, decode, expr, itertools, map } from '@codegrade/utils';
import { backends, boldString, ColumnLayout, ContentBlock, DocumentRoot, MonospaceContent, NewPage, NonBreakingContent, render, Section, SubSection, Table2, Table5, tableCell, } from './document';
import { Highlight } from './highlight';
/** A single case of plagiarism between two submissions. */
export class PlagiarismCase extends api.PlagiarismCase {
    /**
     * @param run The plagiarism run in which this case was created.
     * @param plagCase The plagiarism case.
     */
    constructor(run, plagCase) {
        super(plagCase);
        this.run = run;
        this.assignments = this.assignmentIds.map(assigId => {
            const assig = run.assignments[assigId];
            return { course: run.courses[assig.courseId], ...assig };
        });
    }
    /**
     * Get a message to show when a plagiarism case was found between two distinct assignments.
     *
     * @param index The index of the assignment to show the message for.
     * @returns A message to show for the assignment that is _not_ the assignment
     *     where the plagiarism check was run.
     */
    getOtherAssignmentPlagiarismDesc(index) {
        const assig = this.assignments[index];
        // eslint-disable-next-line
        if (assig && assig.course.virtual) {
            return 'This submission was uploaded during running as part of an archive of old submissions.';
        }
        let desc = `This was submitted to the assignment "${this.assignments[index].name}" of "${this.assignments[index].course.name}"`;
        this.submissions.ifJust(subs => {
            const date = datetime.formatMoment(subs[index].createdAt, 'YYYY-MM-DD');
            desc = `${desc} on ${date}`;
        });
        return desc;
    }
}
/** The colors used to highlight plagiarism matches. */
export const colors = Object.freeze([
    { red: 0, green: 255, blue: 0 },
    { red: 255, green: 0, blue: 0 },
    { red: 0, green: 0, blue: 255 },
    { red: 255, green: 251, blue: 0 },
    { red: 0, green: 255, blue: 255 },
    { red: 127, green: 0, blue: 255 },
]);
/** The default options for the plagiarism report generation. */
export const defaultOptions = {
    contextLines: 5,
    matchesAlign: 'sidebyside',
    newPage: true,
    entireFiles: false,
    outputFormat: 'LaTeX',
};
/**
 * Adjusted a single color channel for the highlight color to be used as a background color.
 *
 * @param colorElement Converts a single color channel value to be used as a background for
 * an highlight.
 * @returns The adjusted channel value.
 */
export function bgColor(colorElement) {
    return Math.round(Math.min(255, colorElement * 0.2 + 0.8 * 255));
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function fgColor(colorElement) {
    return Math.round(colorElement / 2);
}
/**
 * Adjusts the base color to serve as a background color for the highlight and returns
 * the adjusted color with the correct representation.
 *
 * @param color A `Color` object with the `red`, `green`, and `blue` value of the color.
 * @returns A color representation with the adjusted background color.
 */
function getBackgroundColor({ red, green, blue }) {
    return {
        red: bgColor(red),
        green: bgColor(green),
        blue: bgColor(blue),
    };
}
/**
 * Adjusts the base color to serve as a foreground color for the highlight and returns
 * the adjusted color with the correct representation.
 *
 * @param color A `Color` object with the `red`, `green`, and `blue` value of the color.
 * @returns A color representation with the adjusted foreground color.
 */
function getForegroundColor({ red, green, blue }) {
    return {
        red: fgColor(red),
        green: fgColor(green),
        blue: fgColor(blue),
    };
}
/**
 * Adds an highlight to the correct file in the file map.
 * If the file is not present it also adds the file to the map.
 *
 * @param fileMap The map the highlight should be added to.
 * @param fileData The data of the file.
 * @param highlight The highlight to be added.
 */
function addHighlightToFileMap(fileMap, fileData, highlight) {
    fileMap.get(fileData.name).caseOf({
        Just: file => {
            file.highlights.push(highlight);
        },
        Nothing: () => {
            fileMap.set(fileData.name, { ...fileData, highlights: [highlight] });
        },
    });
}
/**
 * Converts the list of plag matches into a list of files with all the highlights for each user.
 *
 * @param plagiarismCase The data about the plagiarism case.
 * @returns For both users a list of files with all the highlights.
 */
function makeHighlightedFiles(plagiarismCase) {
    const usersToFiles = {
        user1: new map.MaybeMap(),
        user2: new map.MaybeMap(),
    };
    plagiarismCase.matches.forEach(m => {
        const highlight = new Highlight(getBackgroundColor(m.color), getForegroundColor(m.color));
        addHighlightToFileMap(usersToFiles.user1, {
            name: m.matchA.name,
            author: plagiarismCase.user1.readableName,
            lines: m.matchA.lines,
        }, { start: m.matchA.startLine + 1, end: m.matchA.endLine, highlight });
        addHighlightToFileMap(usersToFiles.user2, {
            name: m.matchB.name,
            author: plagiarismCase.user2.readableName,
            lines: m.matchB.lines,
        }, { start: m.matchB.startLine + 1, end: m.matchB.endLine, highlight });
    });
    return usersToFiles;
}
/**
 * Creates a code block representation.
 *
 * @param firstLine The number of the first line to be displayed in the code block.
 * @param lines The lines to be displayed.
 * @param fileName The file name.
 * @param userName The author name.
 * @param highlights The highlights to show within the code block.
 * @returns The code block representation with the correct parameters and caption.
 */
function makeCodeBlock(firstLine, lines, fileName, userName, highlights) {
    return {
        firstLine,
        lines,
        caption: new ContentBlock([
            'File ',
            new MonospaceContent(new ContentBlock([fileName])),
            ' of ',
            new NonBreakingContent([userName]),
        ]),
        highlights,
    };
}
/**
 * Makes code blocks for highlighted files.
 *
 * @param file The file to be represented in the code block.
 * @returns A code block containing the file content and highlights.
 */
function makeFileCodeBlocks(file) {
    return makeCodeBlock(1, file.lines, file.name, file.author, file.highlights);
}
/**
 * Creates a tuple of code block representations for both elements of a plagiarism match.
 *
 * @param match A single plagiarism match.
 * @param opts The options for the export.
 * @returns A tuple of codeblocks representing both sides of the match.
 */
function makeMatchCodeBlocks(match, opts) {
    const background = getBackgroundColor(match.color);
    const textColor = getForegroundColor(match.color);
    const highlight = new Highlight(background, textColor);
    const context = opts.contextLines ?? 0;
    /**
     * Creates the code block representation for a single element of the match.
     *
     * @param innerMatch A single element of the match.
     * @returns The code block representation for a single element of the match.
     */
    function maker(innerMatch) {
        const firstLine = Math.max(innerMatch.startLine - context + 1, 1);
        const lines = innerMatch.lines.slice(Math.max(innerMatch.startLine - context, 0), innerMatch.endLine + context);
        return makeCodeBlock(firstLine, lines, innerMatch.name, innerMatch.user.readableName, [
            {
                start: innerMatch.startLine + 1,
                end: innerMatch.endLine,
                highlight,
            },
        ]);
    }
    return [maker(match.matchA), maker(match.matchB)];
}
/**
 * Creates a column layout for pair of nodes if the options call for it,
 * otherwise returns the node sequentially with new pages in between as needed.
 *
 * @param col1 Left side of the column layout.
 * @param col2 Right side of the column layout.
 * @param opts The plagiarism report export options.
 * @returns A list of content nodes, with a column layout if the options call for it,
 * sequential otherwise.
 */
function makeMatchColumnLayout(col1, col2, opts) {
    let children;
    switch (opts.matchesAlign) {
        case 'sidebyside':
            children = [new ColumnLayout([col1, col2])];
            break;
        case 'sequential':
            children = [col1, col2];
            break;
        default:
            return assert.AssertionError.assert(false, 'unknown matches align found');
    }
    if (opts.newPage) {
        children = itertools.flatMap1(children, c => [c, new NewPage()]);
    }
    return children;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function makeMatchSubSection(match, idx, opts) {
    const [b1, b2] = makeMatchCodeBlocks(match, opts);
    // TODO: Discuss if we want to change this section header.
    return new SubSection(`Match ${idx + 1}`, makeMatchColumnLayout(b1, b2, opts));
}
/**
 * If the export options allow, adds new pages in between content nodes.
 *
 * @param blocks The blocks of content to add new pages in between.
 * @returns A list of elements with the new pages added if necessary.
 */
function addNewPages(blocks) {
    return array.flat1(array.mapCustom(blocks, b => [b, new NewPage()], undefined, b => [b]));
}
/**
 * Creates a list of subsection based on code files, and not on matches.
 *
 * @param plagiarismCase The plagiarism case data.
 * @param opts The export options.
 * @returns A list of subsection for code files.
 */
function makeFileSubSections(plagiarismCase, opts) {
    const fileMatches = makeHighlightedFiles(plagiarismCase);
    const filesA = [...fileMatches.user1.values()].map(makeFileCodeBlocks);
    const filesB = [...fileMatches.user2.values()].map(makeFileCodeBlocks);
    return expr.match(opts, 'matchesAlign', {
        sidebyside: () => {
            const maxLength = Math.max(filesA.length, filesB.length);
            const zippedArray = array.zip(array.pad(filesA, maxLength, () => new ContentBlock([''])), array.pad(filesB, maxLength, () => new ContentBlock([''])));
            const columnLayouts = zippedArray.map(cols => new ColumnLayout(cols));
            return [
                new SubSection('Files', opts.newPage ? addNewPages(columnLayouts) : columnLayouts),
            ];
        },
        sequential: () => [
            new SubSection(`${plagiarismCase.user1.readableName}'s Files`, opts.newPage ? addNewPages(filesA).concat(new NewPage()) : filesA),
            new SubSection(`${plagiarismCase.user2.readableName}'s Files`, opts.newPage ? addNewPages(filesB) : filesB),
        ],
    });
}
/**
 * Creates the table representation for the matches overview.
 *
 * @param plagiarismCase The plagiarism case data.
 * @returns A table representation for the match overview.
 */
export function makeMatchesOverviewTable(plagiarismCase) {
    const header = [
        tableCell(boldString(plagiarismCase.user1.readableName)),
        tableCell(boldString('Lines')),
        tableCell(boldString('Color')),
        tableCell(boldString(plagiarismCase.user2.readableName)),
        tableCell(boldString('Lines')),
    ];
    const lines = plagiarismCase.matches.map((match) => [
        tableCell(match.matchA.name),
        tableCell(`${match.matchA.startLine}-${match.matchA.endLine}`),
        tableCell(' ', {
            red: bgColor(match.color.red),
            green: bgColor(match.color.green),
            blue: bgColor(match.color.blue),
        }),
        tableCell(match.matchB.name),
        tableCell(`${match.matchB.startLine}-${match.matchB.endLine}`),
    ]);
    return new Table5([header, ...lines], ['L', 'c', 'c', 'L', 'c'], {
        inner: true,
        outer: false,
    });
}
/**
 * Creates the table representation for frontmatter data.
 *
 * @param param0 The plagiarism case data.
 * @returns A table containing the front matter data.
 */
export function makeFrontMatterTable({ user1, user2, assignment1, assignment2, matchMax, matchAvg, }) {
    return new Table2([
        [
            tableCell(boldString(`Student1 (Left) from ${assignment1.name}`)),
            { content: user1.readableName },
        ],
        [
            tableCell(boldString(`Student2 (Right) from ${assignment2.name}`)),
            tableCell(user2.readableName),
        ],
        [
            tableCell(boldString('Maximum code similarity score:')),
            tableCell(`${matchMax.toFixed(2)}%`),
        ],
        [
            tableCell(boldString('Average code similarity score')),
            tableCell(`${matchAvg.toFixed(2)}%`),
        ],
    ], ['l', 'L']);
}
/**
 * Creates the subsections element for the plagiarism report.
 *
 * @param plagiarismCase The plagiarism case data.
 * @param opts The options for the export of the plagiarism matches.
 * @returns A list of subsection blocks.
 */
export function makeSubSectionBlocks(plagiarismCase, opts) {
    if (opts.entireFiles) {
        return makeFileSubSections(plagiarismCase, opts);
    }
    else {
        return array.mapCustom(plagiarismCase.matches, (match, i) => makeMatchSubSection(match, i, opts), undefined, (match, i) => {
            const el = makeMatchSubSection(match, i, opts);
            if (el.children[el.children.length - 1] instanceof NewPage) {
                return new SubSection(el.heading, el.children.slice(0, -1));
            }
            else {
                return el;
            }
        });
    }
}
/** Representation of a plagiarism report document. */
export class PlagiarismDocument {
    /**
     * @param backend The backend with which to render the document.
     */
    constructor(backend) {
        this.backend = backend;
    }
    /**
     * Renders the plagiarism report.
     *
     * @param plagiarismRun The plagiarism run connected to this case.
     * @param plagiarismCase The plagiarism case data.
     * @param opts Options for the plagiarism report.
     * @returns The rendered report in the chosen format.
     */
    render(plagiarismRun, plagiarismCase, opts) {
        const blocks = makeSubSectionBlocks(plagiarismCase, opts);
        // @ts-expect-error The config object is unknown.
        const lang = plagiarismRun.config.find(([opt, _]) => opt === 'lang')[1];
        const hasBaseCode = 
        // @ts-expect-error The config object is unknown.
        plagiarismRun.config.find(([opt, _]) => opt === 'has_base_code')[1] === 'true';
        const header = {
            left: [
                'CodeGrade Plagiarism Report',
                `Exported on ${datetime.readableFormatDate(moment.utc().local())}`,
                'All highlighted code is similar code',
                // Needed for vertical alignment, LaTeX's \fancyhdr package aligns
                // the left and right parts to the bottom...
                '',
            ],
            right: [
                plagiarismRun.assignment.name,
                'JPLAG version 4.4.0',
                `Language: ${Maybe.fromNullable(lang).orDefault('')}`,
                `Base code included: ${hasBaseCode ? 'Yes' : 'No'}`,
            ],
        };
        const frontMatter = new Section('Summary of the plagiarism check', [
            makeFrontMatterTable(plagiarismCase),
            makeMatchesOverviewTable(plagiarismCase),
        ]);
        const section = new Section('Plagiarism matches', blocks);
        const root = DocumentRoot.makeEmpty().setHeader(header).addChildren([frontMatter, section]);
        return render(this.backend, root);
    }
}
/**
 * Render a plagiarism report for the given matches between the given users.
 *
 * @param plagCase The plagiarism case to make a report for.
 * @param matches The matches to include in the report.
 * @param opts The plagiarism report rendering options.
 * @returns A `Promise` that resolves with the generated `Blob | ArrayBuffer`
 *     containing the file.
 */
export async function renderPlagiarismDocument(plagCase, matches, opts) {
    const neededFileIds = matches.reduce((accum, match) => {
        accum.add(match.files[0].id);
        accum.add(match.files[1].id);
        return accum;
    }, new Set());
    const contents = {};
    neededFileIds.forEach(fileId => {
        contents[fileId] = opts.getFile(fileId).then(data => {
            const content = decode.buffer(data, true);
            return content.split('\n');
        });
    });
    const [user1, user2] = plagCase.users.map(({ id }) => opts.getUser(id));
    const plagMatches = await Promise.all(matches.map(async (match, i) => {
        const [left, right] = await Promise.all([
            contents[match.files[0].id],
            contents[match.files[1].id],
        ]);
        return {
            color: opts.getColor(match, i),
            matchA: {
                startLine: match.lines[0][0],
                endLine: match.lines[0][1] + 1,
                lines: left,
                name: match.files[0].name,
                user: user1,
            },
            matchB: {
                startLine: match.lines[1][0],
                endLine: match.lines[1][1] + 1,
                lines: right,
                name: match.files[1].name,
                user: user2,
            },
        };
    }));
    return new PlagiarismDocument(opts.outputFormat).render(plagCase.run, {
        user1,
        user2,
        assignment1: plagCase.assignments[0],
        assignment2: plagCase.assignments[1],
        matches: plagMatches,
        matchMax: plagCase.matchMax,
        matchAvg: plagCase.matchAvg,
    }, opts);
}
/**
 * Get the filename for a plagiarism report.
 *
 * @param plagCase The plagiarism case for which the report was generated.
 * @param opts The document generation options.
 * @returns The filename to use.
 */
export function getReportFileName(plagCase, opts) {
    const [user1, user2] = plagCase.users.map(u => opts.getUser(u.id).readableName);
    const backend = backends[opts.outputFormat];
    return `plagiarism_case_${user1}+${user2}.${backend.fileExtension}`;
}
